import { Row } from '@unistocks/components/layout/flex';
import { Paragraph } from '@unistocks/components/text/text';
import { ListRenderItem } from '@unistocks/components/virtual-list/list';
import { PortfolioTableRow } from '@unistocks/features/portfolios/components/portfolio-table/portfolio-table-row';
import { PortfolioBase } from '@unistocks/features/portfolios/models/portfolio-base';
import { formatCurrency, t } from '@unistocks/localization';
import { PortfolioDto } from '@unistocks/proxy/axios-client';
import { isHoldings } from '@unistocks/shared/utils/asset-utils';
import { useMemo } from 'react';
import { TableSectionFooter } from './section-footer';
import { TableSectionHeader } from './section-header';
import { PortfolioTableItem } from './types';

export const keyExtractor = (item: PortfolioTableItem) =>
  item.type === 'sectionHeader'
    ? `${item.id}-header`
    : item.type === 'sectionFooter'
      ? `${item.id}-footer`
      : item.id;

export const getPortfolioTableItemType = (item: PortfolioTableItem) => {
  const { type } = item;

  if (type === 'sectionHeader' || type === 'sectionFooter') {
    return type;
  }

  return isHoldings(item) ? 'holdings' : 'cash';
};

export const renderPortfolioTableItem: NonNullable<ListRenderItem<PortfolioTableItem>> = ({
  item,
  index,
  extraData: portfolio
}) => {
  switch (item.type) {
    case 'sectionHeader':
      return <TableSectionHeader portfolio={portfolio} section={item} mt={'$4'} />;

    case 'sectionFooter':
      return <TableSectionFooter portfolio={portfolio} section={item} />;

    default:
      return <PortfolioTableRow portfolio={portfolio} asset={item} index={index} />;
  }
};

export const PortfolioTableFooter = ({ portfolio }: { portfolio?: PortfolioDto }) =>
  portfolio ? (
    <Row bg={'$color4'} justifyContent={'space-between'} mt={'$3'} p={'$2'} br={'$2'}>
      <Paragraph size={'$5'} fontWeight={'$7'}>
        {t('::TotalValue')}
      </Paragraph>
      <Paragraph size={'$5'} fontWeight={'$7'}>
        {formatCurrency(portfolio.calculatedValue, portfolio.currency)}
      </Paragraph>
    </Row>
  ) : null;

PortfolioTableFooter.displayName = 'PortfolioTableFooter';

export const usePortfolioTableData = (portfolio?: PortfolioBase) =>
  useMemo<PortfolioTableItem[]>(
    () =>
      portfolio?.sections.flatMap(section => [
        {
          ...section,
          type: 'sectionHeader'
        },
        ...section.assets,
        {
          ...section,
          type: 'sectionFooter'
        }
      ]) ?? [],
    [portfolio]
  );

/**
 * Find the index of all section headers to make them sticky
 */
export const useStickyHeaderIndicies = (tableData: PortfolioTableItem[]) =>
  tableData.map(({ type }, i) => (type === 'sectionHeader' ? i : null)).filter(Boolean);
